import type { BaseModelAttributes } from "./base";
import type { CreditAccountUserSummary } from "./credit";
import { Locale } from "./locale";
import type { I18nLabels } from "./common";

export enum CreditProductStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export const CreditProductStatusLabels: I18nLabels<CreditProductStatus> = {
  [CreditProductStatus.ACTIVE]: { [Locale.ZH_CN]: "上架", [Locale.EN_US]: "Active" },
  [CreditProductStatus.INACTIVE]: { [Locale.ZH_CN]: "下架", [Locale.EN_US]: "Inactive" },
};

export enum PaymentProvider {
  WECHAT_PAY = "wechat_pay",
}

export enum PaymentOrderStatus {
  PENDING = "pending",
  PAID = "paid",
  CLOSED = "closed",
  FAILED = "failed",
}

export const PaymentOrderStatusLabels: I18nLabels<PaymentOrderStatus> = {
  [PaymentOrderStatus.PENDING]: { [Locale.ZH_CN]: "待支付", [Locale.EN_US]: "Pending" },
  [PaymentOrderStatus.PAID]: { [Locale.ZH_CN]: "已支付", [Locale.EN_US]: "Paid" },
  [PaymentOrderStatus.CLOSED]: { [Locale.ZH_CN]: "已关闭", [Locale.EN_US]: "Closed" },
  [PaymentOrderStatus.FAILED]: { [Locale.ZH_CN]: "失败", [Locale.EN_US]: "Failed" },
};

export interface ICreditProduct extends BaseModelAttributes {
  name: string;
  description: string | null;
  credits: number;
  amountCents: number;
  validityDays: number;
  badge: string | null;
  sortOrder: number;
  status: CreditProductStatus;
}

export interface IPaymentOrder extends BaseModelAttributes {
  userId: string;
  productId: string;
  provider: PaymentProvider;
  status: PaymentOrderStatus;
  outTradeNo: string;
  transactionId: string | null;
  description: string;
  amountCents: number;
  amountPaidCents: number | null;
  credits: number;
  validityDays: number;
  codeUrl: string | null;
  tradeState: string | null;
  paidAt: Date | string | null;
  closedAt: Date | string | null;
  expiresAt: Date | string;
  productSnapshot: Record<string, unknown> | null;
  notifyPayload: Record<string, unknown> | null;
}

export interface CreditProductResponse extends ICreditProduct {}

export interface PaymentOrderResponse extends IPaymentOrder {
  product?: CreditProductResponse;
}

export interface AdminPaymentOrderRow extends PaymentOrderResponse {
  user?: CreditAccountUserSummary;
}

export interface CreditProductQuery {
  search?: string;
  status?: CreditProductStatus;
}

export interface PaymentOrderAdminQuery {
  search?: string;
  userId?: string;
  status?: PaymentOrderStatus;
  provider?: PaymentProvider;
  startDate?: string;
  endDate?: string;
}

export interface UpsertCreditProductRequest {
  name: string;
  description?: string | null;
  credits: number;
  amountCents: number;
  validityDays: number;
  badge?: string | null;
  sortOrder?: number;
  status?: CreditProductStatus;
}

export interface CreatePaymentOrderRequest {
  productId: string;
}

export interface CreatePaymentOrderResponse {
  order: PaymentOrderResponse;
  codeUrl: string;
}

export interface PaymentOrderQuery {
  status?: PaymentOrderStatus;
}
